/**
 * Data Loader Service
 * Loads pre-processed JSON data from Excel file
 *
 * Data Structure Reference: See DATA_MAPPING.md
 */

let cachedData = null;

/**
 * Fetch pre-processed JSON data from public/data
 */
export async function loadExcelData() {
  if (cachedData) {
    console.log('[DataLoader] Using cached data');
    return cachedData;
  }

  try {
    console.log('[DataLoader] Fetching JSON data...');
    const response = await fetch('/data/data.json');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    console.log('[DataLoader] Parsing JSON...');
    cachedData = await response.json();
    console.log('[DataLoader] Data loaded, sheets:', Object.keys(cachedData).length);

    return cachedData;
  } catch (error) {
    console.error('[DataLoader] Error loading data:', error);
    throw error;
  }
}

/**
 * Get sheet names
 */
export function getSheetNames() {
  return cachedData ? Object.keys(cachedData) : [];
}

// ============================================================================
// HELPER: Extract data from sheet with Pattern A (single year, 2024)
// Data in column 1, labels in column 0
// ============================================================================
function extractPatternA(sheetName, startRow, endRow, isPercentage = true) {
  const sheet = cachedData?.[sheetName];
  if (!sheet) return { labels: [], values: [] };

  const labels = [];
  const values = [];

  for (let i = startRow; i <= endRow; i++) {
    const row = sheet[i];
    if (!row || !row[0] || row[0] === 'Total general') continue;
    if (typeof row[1] !== 'number') continue;

    // Clean label: remove line breaks and extra whitespace
    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    values.push(isPercentage ? Math.round(row[1] * 100) : parseFloat(row[1].toFixed(2)));
  }

  return { labels, values };
}

// ============================================================================
// HELPER: Extract data from multi-year table (Pattern B)
// Data in column 3 (2024), labels in column 0
// ============================================================================
function extractPatternB(sheetName, startRow, endRow, col2024 = 3) {
  const sheet = cachedData?.[sheetName];
  if (!sheet) return { labels: [], values: [] };

  const labels = [];
  const values = [];

  for (let i = startRow; i <= endRow; i++) {
    const row = sheet[i];
    if (!row || !row[0] || row[0] === 'Total general') continue;
    if (typeof row[col2024] !== 'number') continue;

    // Clean label: remove line breaks and extra whitespace
    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    values.push(Math.round(row[col2024] * 100));
  }

  return { labels, values };
}

// ============================================================================
// Q1: Industry Distribution (Multi-year: 2023, 2024, 2025)
// Rows 16-22, columns: 0=label, 1=2023, 2=2024, 3=2025
// ============================================================================
export function getIndustryDistribution() {
  const sheet = cachedData?.['Q1.Distribución por industria'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 16; i <= 22; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q2: Employee Distribution (Multi-year: 2023, 2024, 2025)
// Rows 10-13, columns: 0=label, 2=2023, 3=2024, 4=2025
// (Using columns 2,3,4 to extract 2023-2025; stored as data2022,data2023,data2024 for backward compatibility)
// ============================================================================
export function getEmployeeDistribution() {
  const sheet = cachedData?.['Q2. Empleados'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 10; i <= 13; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q3: Billing Distribution (Multi-year: 2022, 2023, 2024)
// Rows 13-18, columns: 0=label, 1=2022, 2=2023, 3=2024
// ============================================================================
export function getBillingDistribution() {
  const sheet = cachedData?.['Q3. Facturación'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 13; i <= 18; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q4: Fill Rate Sales (Multi-year: 2023, 2024, 2025)
// 3-year table at rows 20-26, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys
// for backward compatibility with the chart component).
// ============================================================================
export function getFillRateSales() {
  const sheet = cachedData?.['|Q4. % Fill rate de ventas'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 20; i <= 26; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q5: Perfect Sales Order (Multi-year: 2022, 2023, 2024)
// 3-year table at rows 23-29, columns: 0=label, 1=2022, 2=2023, 3=2024
// ============================================================================
export function getPerfectSalesOrder() {
  const sheet = cachedData?.['|Q5. Orden de venta perfecta'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 23; i <= 29; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q6: Perfect Order by Employees (Multi-year: 2023, 2024, 2025)
// Table at rows 21-26, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getPerfectOrderByEmployees() {
  const sheet = cachedData?.['Q6. Orden perfecta x #empl'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 21; i <= 26; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q7: Forecast Accuracy (3 years: 2023, 2024, 2025)
// Table at rows 21-27, columns: 0=label, 1=2023, 2=2024, 3=2025
// ============================================================================
export function getForecastAccuracy() {
  const sheet = cachedData?.['|Q7. Forecast accuracy'];
  if (!sheet) return { labels: [], data2023: [], data2024: [], data2025: [] };

  const labels = [];
  const data2023 = [];
  const data2024 = [];
  const data2025 = [];

  for (let i = 21; i <= 27; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2023.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2024.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2025.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2023, data2024, data2025 };
}

// ============================================================================
// Q8: Fill Rate Purchases (3 years: 2022, 2023, 2024)
// 3-year table at rows 17-23, columns: 0=label, 1=2022, 2=2023, 3=2024
// ============================================================================
export function getFillRatePurchases() {
  const sheet = cachedData?.['|Q8. fill rate compras'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 17; i <= 23; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q9: On-time Supplier Deliveries (Multi-year: 2023, 2024, 2025)
// Table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getSupplierDeliveries() {
  const sheet = cachedData?.['|Q9. Entregas a tiempo de prov'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q10: Inventory Rotation (Multi-year: 2023, 2024, 2025) - NOT percentage
// Table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getInventoryRotation() {
  const sheet = cachedData?.['Q10. Rotación de inventario'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? parseFloat(row[2].toFixed(1)) : 0);
    data2023.push(typeof row[3] === 'number' ? parseFloat(row[3].toFixed(1)) : 0);
    data2024.push(typeof row[4] === 'number' ? parseFloat(row[4].toFixed(1)) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q11: Warehouse Utilization (3 years: 2022, 2023, 2024)
// 3-year table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024
// ============================================================================
export function getWarehouseUtilization() {
  const sheet = cachedData?.['Q11. %Utiliz almacén'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q12: Warehouse Outsourcing (3 years: 2023, 2024, 2025)
// Table at rows 19-25, columns: 0=label, 1=2023, 2=2024, 3=2025
// ============================================================================
export function getWarehouseOutsourcing() {
  const sheet = cachedData?.['|Q12. Tercerización almac'];
  if (!sheet) return { labels: [], data2023: [], data2024: [], data2025: [] };

  const labels = [];
  const data2023 = [];
  const data2024 = [];
  const data2025 = [];

  for (let i = 19; i <= 25; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2023.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2024.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2025.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2023, data2024, data2025 };
}

// ============================================================================
// Q13: Transport Outsourcing (Multi-year: 2023, 2024, 2025)
// Table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getTransportOutsourcing() {
  const sheet = cachedData?.['|Q13. Transporte tercerizado'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q14: Container Utilization (3 years: 2022, 2023, 2024)
// 3-year table at rows 19-25, columns: 0=label, 1=2022, 2=2023, 3=2024
// ============================================================================
export function getContainerUtilization() {
  const sheet = cachedData?.['|Q14. Utilización de conten.'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 19; i <= 25; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2023.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2024.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q15: Transport On-time Delivery (Multi-year: 2023, 2024, 2025)
// Table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getTransportDelivery() {
  const sheet = cachedData?.['|Q15. Entregas a tiempo transp'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q16: Personnel Rotation (Multi-year: 2023, 2024, 2025)
// Table at rows 19-25, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getPersonnelRotation() {
  const sheet = cachedData?.['Q16.Rot pers Log.'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 19; i <= 25; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q17-18: Carbon Footprint Measurement (2023 only)
// Data at rows 15-19, column 1 = percentage
// ============================================================================
export function getCarbonMeasurement() {
  const sheet = cachedData?.['Q17 &18. Medic huella de carb '];
  if (!sheet) return { labels: [], values: [] };

  const labels = [];
  const values = [];

  for (let i = 15; i <= 19; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    values.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
  }

  return { labels, values };
}

// ============================================================================
// Q20: Carbon Footprint (2025 - TON CO2)
// Data at rows 5-11, column 2 = TON CO2
// ============================================================================
export function getCarbonFootprint() {
  const sheet = cachedData?.['Q20. Huella de carbono'];
  if (!sheet) return { labels: [], values: [] };

  const labels = [];
  const values = [];

  for (let i = 5; i <= 11; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    values.push(typeof row[2] === 'number' ? row[2] : 0);
  }

  return { labels, values };
}

// ============================================================================
// Q21: Payroll Cost (3 years: 2023, 2024, 2025)
// Data at rows 21-27, columns: 0=label, 1=2023, 2=2024, 3=2025
// ============================================================================
export function getPayrollCost() {
  const sheet = cachedData?.['Q21. Costo nómina log'];
  if (!sheet) return { labels: [], data2023: [], data2024: [], data2025: [] };

  const labels = [];
  const data2023 = [];
  const data2024 = [];
  const data2025 = [];

  for (let i = 21; i <= 27; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2023.push(typeof row[1] === 'number' ? Math.round(row[1] * 100) : 0);
    data2024.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2025.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
  }

  return { labels, data2023, data2024, data2025 };
}

// ============================================================================
// Q22: Transport Cost (Multi-year: 2023, 2024, 2025)
// Data at rows 22-28, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getTransportCost() {
  const sheet = cachedData?.['Q22.Cto. transporte '];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 22; i <= 28; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q23: Distribution Cost (Multi-year: 2023, 2024, 2025)
// Table at rows 18-24, columns: 0=label, 1=2022, 2=2023, 3=2024, 4=2025
// Chart shows 2023-2025, so read columns 2,3,4 (kept in data2022/23/24 keys).
// ============================================================================
export function getDistributionCost() {
  const sheet = cachedData?.['Q23. |Cto. distribución'];
  if (!sheet) return { labels: [], data2022: [], data2023: [], data2024: [] };

  const labels = [];
  const data2022 = [];
  const data2023 = [];
  const data2024 = [];

  for (let i = 18; i <= 24; i++) {
    const row = sheet[i];
    if (!row || !row[0]) continue;

    const label = String(row[0]).replace(/[\r\n]+/g, ' ').trim();
    labels.push(label);
    data2022.push(typeof row[2] === 'number' ? Math.round(row[2] * 100) : 0);
    data2023.push(typeof row[3] === 'number' ? Math.round(row[3] * 100) : 0);
    data2024.push(typeof row[4] === 'number' ? Math.round(row[4] * 100) : 0);
  }

  return { labels, data2022, data2023, data2024 };
}

// ============================================================================
// Q24: Three Costs (Transport, Distribution, Total Logistics)
// Data in columns K-T (index 11-20) for each year
// 2022: Rows 6-13, 2023: Rows 17-23, 2024: Rows 28-34
// Column 11=Industry, 13=Transport Avg, 16=Distribution Avg, 19=Logistics Total Avg
// ============================================================================
export function getThreeCosts() {
  const sheet = cachedData?.['Q24. 3 costos'];
  if (!sheet) return {
    labels: [],
    transport2022: [], distribution2022: [], logistics2022: [],
    transport2023: [], distribution2023: [], logistics2023: [],
    transport2024: [], distribution2024: [], logistics2024: []
  };

  // Helper to normalize labels (remove line breaks and collapse multiple spaces)
  const normalizeLabel = (str) => String(str).replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();

  const labels = [];
  const transport2022 = [], distribution2022 = [], logistics2022 = [];
  const transport2023 = [], distribution2023 = [], logistics2023 = [];
  const transport2024 = [], distribution2024 = [], logistics2024 = [];

  // Extract 2022 data (rows 6-12, column K=11 for industry)
  for (let i = 6; i <= 12; i++) {
    const row = sheet[i];
    if (!row || !row[11]) continue;

    const label = normalizeLabel(row[11]);
    labels.push(label);
    transport2022.push(typeof row[13] === 'number' ? Math.round(row[13] * 100) : 0);
    distribution2022.push(typeof row[16] === 'number' ? Math.round(row[16] * 100) : 0);
    logistics2022.push(typeof row[19] === 'number' ? Math.round(row[19] * 100) : 0);
  }

  // Extract 2023 data (rows 17-23, column K=11 for industry)
  for (let i = 17; i <= 23; i++) {
    const row = sheet[i];
    if (!row || !row[11]) continue;

    const label = normalizeLabel(row[11]);
    const labelIndex = labels.indexOf(label);
    if (labelIndex === -1) continue;

    transport2023[labelIndex] = typeof row[13] === 'number' ? Math.round(row[13] * 100) : 0;
    distribution2023[labelIndex] = typeof row[16] === 'number' ? Math.round(row[16] * 100) : 0;
    logistics2023[labelIndex] = typeof row[19] === 'number' ? Math.round(row[19] * 100) : 0;
  }

  // Extract 2024 data (rows 28-34, column K=11 for industry)
  for (let i = 28; i <= 34; i++) {
    const row = sheet[i];
    if (!row || !row[11]) continue;

    const label = normalizeLabel(row[11]);
    const labelIndex = labels.indexOf(label);
    if (labelIndex === -1) continue;

    transport2024[labelIndex] = typeof row[13] === 'number' ? Math.round(row[13] * 100) : 0;
    distribution2024[labelIndex] = typeof row[16] === 'number' ? Math.round(row[16] * 100) : 0;
    logistics2024[labelIndex] = typeof row[19] === 'number' ? Math.round(row[19] * 100) : 0;
  }

  return {
    labels,
    transport2022, distribution2022, logistics2022,
    transport2023, distribution2023, logistics2023,
    transport2024, distribution2024, logistics2024
  };
}

// ============================================================================
// Dashboard Info Boxes: headline stats computed dynamically from 2025 data
// Feeds the six summary cards in InfoBoxes.js
// ============================================================================

// Clean a label: strip line breaks / collapse whitespace
function cleanLabel(value) {
  return String(value).replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ').trim();
}

// Round a 0-1 ratio to a whole percent. The tiny epsilon absorbs float noise
// (e.g. 0.935 stored as 0.9349999999998) so half-values round up like Excel.
function toPercent(value) {
  return Math.round(value * 100 + 1e-9);
}

// Collect { label, pct } for each sector row in a block, reading `col` as the value
function sectorSeries(sheetName, startRow, endRow, col) {
  const sheet = cachedData?.[sheetName];
  if (!sheet) return [];
  const out = [];
  for (let i = startRow; i <= endRow; i++) {
    const row = sheet[i];
    if (!row || !row[0] || typeof row[col] !== 'number') continue;
    out.push({ label: cleanLabel(row[0]), pct: toPercent(row[col]) });
  }
  return out;
}

export function getDashboardStats() {
  const stats = {
    topIndustryLabel: 'Retail',
    topIndustryShare: null,
    fillRateSectors: ['Automotriz', 'Manufactura', 'Servicios logísticos', 'Retail'],
    fillRate: null,
    billingOver250: null,
    rotationSector: 'Automotriz',
    rotationValue: null,
    perfectOrderSector: 'Sector salud/ Farmacéuticas',
    perfectOrder: null,
    deliveryCommon: null,
    deliveryExceptions: [],
  };

  // Box 1: most-represented industry (Q1, 2025 = column 3, rows 16-22)
  const q1 = cachedData?.['Q1.Distribución por industria'];
  if (q1) {
    let best = -1;
    for (let i = 16; i <= 22; i++) {
      const row = q1[i];
      if (!row || !row[0] || typeof row[3] !== 'number') continue;
      if (row[3] > best) {
        best = row[3];
        stats.topIndustryLabel = cleanLabel(row[0]);
      }
    }
    if (best >= 0) stats.topIndustryShare = toPercent(best);
  }

  // Box 2: best fill-rate sector(s) in 2025 (Q4, rows 20-26, column 4 = 2025)
  // Multiple sectors can tie at the top, so collect all that reach the max.
  const fillRates = sectorSeries('|Q4. % Fill rate de ventas', 20, 26, 4);
  if (fillRates.length) {
    const best = Math.max(...fillRates.map((s) => s.pct));
    stats.fillRate = best;
    stats.fillRateSectors = fillRates.filter((s) => s.pct === best).map((s) => s.label);
  }

  // Box 3: % of companies billing > 250 MDP (Q3, 2025 block: rows 5-7 hold the ≤250 buckets)
  const q3 = cachedData?.['Q3. Facturación'];
  if (q3) {
    let under = 0;
    let ok = true;
    for (let i = 5; i <= 7; i++) {
      const row = q3[i];
      if (!row || typeof row[1] !== 'number') { ok = false; break; }
      under += row[1];
    }
    if (ok) stats.billingOver250 = toPercent(1 - under);
  }

  // Box 4: sector with the LOWEST personnel rotation in 2025 (best retention)
  // Q16, rows 19-25, column 4 = 2025
  const rotation = sectorSeries('Q16.Rot pers Log.', 19, 25, 4);
  if (rotation.length) {
    const lowest = rotation.reduce((a, b) => (b.pct < a.pct ? b : a));
    stats.rotationSector = lowest.label;
    stats.rotationValue = lowest.pct;
  }

  // Box 5: sector with the HIGHEST perfect-order rate in 2025
  // Q5, rows 6-12 (7 sectors, excludes "Total general"), column 1 = 2025
  const perfectOrder = sectorSeries('|Q5. Orden de venta perfecta', 6, 12, 1);
  if (perfectOrder.length) {
    const top = perfectOrder.reduce((a, b) => (b.pct > a.pct ? b : a));
    stats.perfectOrderSector = top.label;
    stats.perfectOrder = top.pct;
  }

  // Box 6: on-time transport delivery in 2025 (Q15, rows 18-24, column 4 = 2025)
  // Report the most common value and any sectors that fall below it.
  const deliveries = sectorSeries('|Q15. Entregas a tiempo transp', 18, 24, 4);
  if (deliveries.length) {
    const counts = {};
    deliveries.forEach((s) => { counts[s.pct] = (counts[s.pct] || 0) + 1; });
    const common = Number(Object.keys(counts).reduce((a, b) => (counts[b] > counts[a] ? b : a)));
    stats.deliveryCommon = common;
    stats.deliveryExceptions = deliveries.filter((s) => s.pct !== common);
  }

  return stats;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get raw sheet data for custom parsing
 */
export function getRawSheet(sheetName) {
  return cachedData?.[sheetName] || [];
}

/**
 * Check if data is loaded
 */
export function isDataLoaded() {
  return cachedData !== null;
}
